<template>
  <div class="rule" :class="{ mobile: isMobile }">
    <h6 v-if="!(isEditingAlert && isMobile)">
      <span v-if="isMobile" @click="backToIndexPage">
        <fa icon="chevron-left" class="icon"></fa>
      </span>
      Rule
    </h6>
    <p>Trigger the alert if any of the selected condition(s) happen.</p>
    <span v-if="isMobile" class="separate-line"></span>
    <p v-show="isRuleEmpty" class="text-danger warning">
      At least one rule must be selected.
    </p>
    <div class="form-check">
      <input
        class="form-check-input"
        type="checkbox"
        v-model="prevAlertRule.isTriggeredByFirstHit"
        id="isTriggeredByFirstHit"
      />
      <label class="form-check-label" for="isTriggeredByFirstHit">
        Trigger the alert when any of the selected prefix(es) send hit(s) for
        the first time.
      </label>
    </div>
    <div class="form-check">
      <input
        class="form-check-input"
        type="checkbox"
        v-model.number="prevAlertRule.isTriggeredByHitNumber"
        id="isTriggeredByHitNumber"
      />
      <label class="form-check-label" for="isTriggeredByHitNumber">
        Trigger the alert when any of the prefixes selected send the same or
        more than
      </label>
    </div>
    <div
      class="form-group"
      :class="{ disabled: !prevAlertRule.isTriggeredByHitNumber }"
    >
      <input
        type="number"
        inputmode="numeric"
        pattern="[0-9]*"
        class="form-control "
        :class="{
          warning: !isHitNumberValid,
          disabled: !prevAlertRule.isTriggeredByHitNumber,
          wider:
            prevAlertRule.hitNumberPerDay && prevAlertRule.hitNumberPerDay > 999
        }"
        :disabled="!prevAlertRule.isTriggeredByHitNumber"
        v-model.number="prevAlertRule.hitNumberPerDay"
      />hit(s) within a day.
      <p v-show="!isHitNumberValid" class="text-danger warning">
        {{ warningText }}
      </p>
    </div>
    <div
      v-if="economyName && averageHitOfSelectedASN !== undefined"
      class="info"
    >
      <img src="../../../assets/light-bulb.png" alt="insight" />
      <span>Insight</span>
      <p>
        On average, {{ economyName }} sent {{ averageHitOfSelectedASN }} hits
        per 24 hours in the last {{ averageHitPeriod }} days.
      </p>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { DAYS_OF_CURRENT_HITS_PERIOD } from '../../../constants/alertsConstant';
import { checkIsInteger } from '../../../utils/common';

export default {
  name: 'Rule',
  props: ['prevAlertRule'],
  data() {
    return {
      economyName: '',
      averageHitPeriod: DAYS_OF_CURRENT_HITS_PERIOD
    };
  },
  computed: {
    ...mapGetters(['isMobile']),
    ...mapState('dataStore', ['selectedASN']),
    ...mapGetters('alertsSuspiciousTrafficStore', ['averageHitOfSelectedASN']),
    ...mapState('alertsSuspiciousTrafficStore', ['isEditingAlert']),
    isHitNumberValid() {
      return (
        this.prevAlertRule.hitNumberPerDay > 0 &&
        this.prevAlertRule.hitNumberPerDay < 1000001 &&
        checkIsInteger(this.prevAlertRule.hitNumberPerDay)
      );
    },
    warningText() {
      if (this.prevAlertRule.hitNumberPerDay <= 0)
        return 'It must be an integer greater than zero.';
      if (this.prevAlertRule.hitNumberPerDay > 1000000)
        return 'It must be an integer not greater than 1,000,000.';
      if (!checkIsInteger(this.prevAlertRule.hitNumberPerDay)) {
        return 'It must be an integer greater than zero.';
      }

      return '';
    },
    isRuleEmpty() {
      return (
        !this.prevAlertRule.isTriggeredByHitNumber &&
        !this.prevAlertRule.isTriggeredByFirstHit
      );
    }
  },
  methods: {
    backToIndexPage() {
      this.$emit('backToIndexPage');
    }
  },
  mounted() {
    this.economyName = this.selectedASN && this.selectedASN.economyName;
  }
};
</script>

<style lang="scss" scoped>
.rule {
  position: relative;
  padding: 2rem 1.5rem;
  height: 100%;
  overflow-y: scroll;
  overflow-x: hidden;

  & > p {
    line-height: 1.5rem;
  }
}

h6 {
  font-weight: 600;

  & > span {
    font-size: 1.2rem;
    padding: 0.5rem 1rem;
    margin-left: -1rem;
    vertical-align: bottom;
    color: $text-light-blue;
  }
}

.separate-line {
  @include separate-line;
}

p.warning {
  margin-top: -0.8rem;
  position: absolute;
}

.form-check {
  margin: 2rem 1rem;

  input {
    width: 1.2rem;
    height: 1.2rem;
    margin-top: 0.2rem;
  }

  label {
    margin-left: 0.5rem;
  }
}

.form-group {
  position: relative;
  margin-top: -1.5rem;
  margin-left: 3rem;

  input {
    display: inline-block;
    text-align: center;
    margin-right: 0.5rem;
    width: 3.5rem;
    transition: width 0.5s;
  }

  input.wider {
    width: 7rem;
  }

  input.warning {
    border-color: #b00020;
    box-shadow: 0 0 0 0.2rem rgba(176, 0, 32, 0.25);
    -webkit-box-shadow: 0 0 0 0.2rem rgba(176, 0, 32, 0.25);
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type='number'] {
    -moz-appearance: textfield;
  }

  &.disabled,
  input.disabled {
    text-decoration: line-through;
  }

  p.warning {
    margin-top: 0.1rem;
    position: absolute;
  }
}

.info {
  margin-top: 1.5rem;
  margin-left: 3rem;

  img {
    margin-right: 0.5rem;
    vertical-align: top;
  }

  span {
    font-weight: 600;
  }

  p {
    margin-top: 0.5rem;
  }
}

.mobile {
  padding: 1rem;

  h6 {
    margin-bottom: 1rem;
  }

  p.warning {
    margin-top: -1.2rem;
  }

  .form-check {
    margin: 0.5rem 0 1rem 0;
  }

  .form-group {
    margin: -0.5rem 1.5rem;

    p.warning {
      margin-top: 0.2rem;
    }
  }
  .info {
    margin: 2rem 1.5rem 0;
  }
}
</style>
